import { useCancelInvoiceMutation } from "./useCancelInvoiceMutation";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { AxiosError } from "axios";
import { useModals } from "../../../../context/ModalContext";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Domain hook for cancelling unpaid invoices.
 * Maps 400, 403, 404 to readable snackbar messages.
 */

export const useCancelInvoice = () => {

  const qc = useQueryClient();
  const mutation = useCancelInvoiceMutation();
  const { showSnackbar } = useGlobalSnackbar();
  const { closeByKey } = useModals();
  const getErrorMessage = (error: unknown): string => {
    if (error instanceof AxiosError) {
      const message = (error.response?.data as any)?.message ?? error.message;
      const status = error.response?.status;
      switch (status) {
        case 400:
          return message || "This invoice cannot be cancelled.";
        case 403:
          return message || "You do not have permission to cancel this invoice.";
        case 404:
          return message || "Invoice not found.";
        default:
          return message || "An unexpected error occurred. Please try again.";
      }
    }
    if (error instanceof Error) return error.message;
    return "An unexpected error occurred. Please try again.";
  };

  const cancelInvoice = (
    invoiceUid: string,
    options?: {
      billingJobEventUids?: string[];
      /** Visit / job-event UIDs to refresh (row pill) when invoice payload omits billingJobEvents */
      extraInvalidateJobEventUids?: string[];
      /** Deposit invoice: invalidates per-quote deposit list + quote table */
      quoteId?: number | null;
      invoiceModal?: string;
    },
  ) => {
    mutation.mutate(
      {
        invoiceUid,
        billingJobEventUids: options?.billingJobEventUids,
        extraInvalidateJobEventUids: options?.extraInvalidateJobEventUids,
        quoteId: options?.quoteId,
      },
      {
        onSuccess: () => {
          showSnackbar("Invoice cancelled successfully", "success", 5000, "top-center");
          if (options?.invoiceModal) {
            closeByKey(options.invoiceModal);
          }
          qc.invalidateQueries({ queryKey: ["invoice-metrics"] });

        },
        onError: (error) => {
          showSnackbar(getErrorMessage(error), "error", 6000, "top-center");
        },
      },
    );
  };

  return {
    cancelInvoice,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    data: mutation.data,
  };
};
